// Phase 9.2 — Auto-suggest wizard (5 steps).
//
// Emits POST /api/fnb/pos/auto-suggest with format/scope/etc, gets
// back an array of persisted draft POs, and navigates to the PO
// history page filtered to today so the user can review + release.

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, detailFromError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtINR } from "./poStatusMeta";
import { Info } from "lucide-react";

// Phase 9.7 (fix) — pretty-print a stock/order qty. Whole numbers drop
// the .0; fractional keeps up to 3 decimals to match backend precision.
const fmtQty = (n) => {
  const v = Number(n || 0);
  if (Number.isInteger(v)) return String(v);
  return v.toFixed(3).replace(/\.?0+$/, "");
};

const STATUSES = [
  { key: "out_of_stock", label: "Out of Stock", default: true },
  { key: "danger", label: "Danger", default: true },
  { key: "reorder", label: "Reorder", default: true },
  { key: "unknown", label: "Unknown", default: false },
];

// Phase 9.6.3 — explicit format-mode toggle. Wording matches the
// product brief exactly so the operator sees the same copy the
// analyst signed off on.
const FORMAT_MODES = [
  {
    key: "auto",
    label: "Auto (recommended)",
    hint: "Pan-India when a vendor covers 2+ outlets, Location-wise otherwise",
  },
  {
    key: "per_location",
    label: "One PO per location",
    hint: "always split; each outlet gets its own PO from every vendor",
  },
  {
    key: "combined",
    label: "Combined per vendor",
    hint: "always merge; each vendor gets one Pan-India PO covering all outlets",
  },
];

const FORMAT_MODE_LABELS = {
  auto: "Auto",
  per_location: "One PO per location",
  combined: "Combined per vendor",
};

export default function FnbPoAutoSuggest() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState(1);
  const [format, setFormat] = useState("pan_india");
  // Phase 9.6.3 — explicit format-mode toggle. Default `auto` matches
  // the pre-9.6.3 behaviour so nothing changes for users who don't
  // touch the toggle.
  const [formatMode, setFormatMode] = useState("auto");
  // Phase 9.7 — order strategy. `consumption` (ADC × TAT) is the
  // pre-9.7 default; `file_rules` uses fb_item_ordering_config.
  // The default flips to `file_rules` iff the config table is
  // non-empty (populated via the Ordering Rules master data page).
  const [strategy, setStrategy] = useState("consumption");
  const [configCount, setConfigCount] = useState(null);
  const [scope, setScope] = useState("company");
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [statusSet, setStatusSet] = useState(() =>
    new Set(STATUSES.filter((s) => s.default).map((s) => s.key)),
  );
  const [vendorSelection, setVendorSelection] = useState("cheapest");
  const [explicitVendors, setExplicitVendors] = useState([]);
  const [category, setCategory] = useState(searchParams.get("category") || "");
  // Phase 9.4b — item_scope. `daily` (default) excludes bar + junk;
  // `bar` deep-linked from the Bar Stock tab; `all` when the user
  // checks "Include bar items".
  const [itemScope, setItemScope] = useState(() => {
    const raw = searchParams.get("item_scope");
    return ["daily", "bar", "all"].includes(raw) ? raw : "daily";
  });
  const [locations, setLocations] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get("/fnb/locations"),
      api.get("/fnb/vendors"),
    ]).then(([l, v]) => {
      if (cancelled) return;
      setLocations(l.data.filter(
        // Phase 9.7.2 — use `is_active` (manual override) not the
        // legacy `is_po_target` flag as the destination gate.
        (x) => x.active !== false && x.is_active !== false && !x.is_warehouse,
      ));
      setVendors(v.data);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Prefill from URL params (deep-link from Reorder page)
  useEffect(() => {
    const scopeQP = searchParams.get("scope");
    if (scopeQP === "location") {
      setScope("location");
      const loc = searchParams.get("location");
      if (loc) setSelectedLocations([loc]);
    }
    const statusQP = searchParams.get("status");
    if (statusQP) setStatusSet(new Set([statusQP]));
  }, [searchParams]);

  // Phase 9.7 — resolve default strategy based on whether the config
  // table is populated. Non-empty → default `file_rules` (recommended).
  // Empty → default `consumption` (pre-9.7 behavior).
  useEffect(() => {
    let cancelled = false;
    api.get("/fnb/item-ordering-config?limit=1").then((res) => {
      if (cancelled) return;
      const total = res.data?.total ?? 0;
      setConfigCount(total);
      if (total > 0) setStrategy("file_rules");
    }).catch(() => { if (!cancelled) setConfigCount(0); });
    return () => { cancelled = true; };
  }, []);

  const runPreview = async () => {
    if (strategy === "consumption" && statusSet.size === 0) {
      toast.error("Pick at least one status to include");
      return;
    }
    if (scope === "location" && selectedLocations.length === 0) {
      toast.error("Pick at least one location");
      return;
    }
    setLoading(true);
    try {
      const body = {
        format, scope,
        location_ids: scope === "location" ? selectedLocations : null,
        status_filter: Array.from(statusSet),
        ignore_not_applicable: true,
        vendor_selection: vendorSelection,
        explicit_vendor_ids: vendorSelection === "explicit" ? explicitVendors : null,
        category: category || null,
        item_scope: itemScope,
        format_mode: formatMode,
        strategy,
      };
      // First pass: dry run
      const res = await api.post("/fnb/pos/auto-suggest?persist=false", body);
      setPreview(res.data);
      setStep(5);
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setLoading(false);
    }
  };

  const commit = async () => {
    if (!preview || preview.drafts.length === 0) return;
    setSubmitting(true);
    try {
      const body = {
        format, scope,
        location_ids: scope === "location" ? selectedLocations : null,
        status_filter: Array.from(statusSet),
        ignore_not_applicable: true,
        vendor_selection: vendorSelection,
        explicit_vendor_ids: vendorSelection === "explicit" ? explicitVendors : null,
        category: category || null,
        item_scope: itemScope,
        format_mode: formatMode,
        strategy,
      };
      const res = await api.post("/fnb/pos/auto-suggest?persist=true", body);
      const n = res.data?.created?.length || 0;
      toast.success(`${n} draft PO${n === 1 ? "" : "s"} created`);
      navigate("/tools/fnb-po/pos");
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setSubmitting(false);
    }
  };

  const toggleStatus = (k) => {
    const next = new Set(statusSet);
    if (next.has(k)) next.delete(k); else next.add(k);
    setStatusSet(next);
  };

  const toggleLoc = (id) => {
    setSelectedLocations((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleVendor = (id) => {
    setExplicitVendors((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  return (
    <div className="space-y-6" data-testid="fnb-po-auto-suggest">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Auto-suggest POs</h1>
        <p className="text-sm text-muted-foreground">
          Step {step} of 5
        </p>
      </header>

      {step === 1 && (
        <Card data-testid="wizard-step-1">
          <CardHeader>
            <div className="text-sm font-medium">1 · Order strategy &amp; PO format</div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Phase 9.7 — Order strategy toggle */}
            <div className="space-y-2">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Order strategy
              </div>
              <div
                className="space-y-2"
                role="radiogroup"
                aria-label="Order strategy"
                data-testid="strategy-radio-group"
              >
                <label
                  className={`flex items-start gap-3 border rounded-md px-3 py-2.5 cursor-pointer text-sm ${
                    strategy === "file_rules"
                      ? "border-primary bg-primary/5"
                      : "border-border/70 hover:bg-muted/40"
                  } ${configCount === 0 ? "opacity-60" : ""}`}
                  data-testid="strategy-file-rules"
                >
                  <input
                    type="radio" name="strategy" className="mt-1"
                    checked={strategy === "file_rules"}
                    onChange={() => setStrategy("file_rules")}
                    disabled={configCount === 0}
                    data-testid="strategy-file-rules-input"
                  />
                  <span className="flex flex-col leading-tight">
                    <span className="font-medium">
                      File rules{" "}
                      {configCount === null ? null : configCount > 0 && (
                        <span className="text-[10px] text-muted-foreground font-normal">
                          (recommended · {configCount} items configured)
                        </span>
                      )}
                    </span>
                    <span className="text-xs text-muted-foreground mt-0.5">
                      — Uses the reorder levels + fixed purchase quantities from
                      the Ordering Rules upload. Only covers items in that file.
                    </span>
                    {configCount === 0 && (
                      <span className="text-[11px] text-amber-800 mt-1">
                        No ordering rules uploaded yet.{" "}
                        <Link
                          to="/tools/fnb-po/ordering-rules"
                          className="underline"
                          data-testid="strategy-file-rules-link"
                        >
                          Upload the file
                        </Link>{" "}
                        to enable this mode.
                      </span>
                    )}
                  </span>
                </label>
                <label
                  className={`flex items-start gap-3 border rounded-md px-3 py-2.5 cursor-pointer text-sm ${
                    strategy === "consumption"
                      ? "border-primary bg-primary/5"
                      : "border-border/70 hover:bg-muted/40"
                  }`}
                  data-testid="strategy-consumption"
                >
                  <input
                    type="radio" name="strategy" className="mt-1"
                    checked={strategy === "consumption"}
                    onChange={() => setStrategy("consumption")}
                    data-testid="strategy-consumption-input"
                  />
                  <span className="flex flex-col leading-tight">
                    <span className="font-medium">Consumption-based</span>
                    <span className="text-xs text-muted-foreground mt-0.5">
                      — Uses ADC × TAT math over 7/14/30/90-day sales.
                      Covers all items.
                    </span>
                  </span>
                </label>
              </div>
            </div>

            {/* PO format-mode toggle */}
            <div className="space-y-2">
              <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                PO format
              </div>
              <div className="text-xs text-muted-foreground">
                Choose how draft POs should be grouped across the vendors and
                outlets the wizard picks up.
              </div>
              <div
                className="space-y-2"
                role="radiogroup"
                aria-label="PO format mode"
                data-testid="format-mode-radio-group"
              >
                {FORMAT_MODES.map((m) => (
                  <label
                    key={m.key}
                    className={`flex items-start gap-3 border rounded-md px-3 py-2.5 cursor-pointer text-sm ${
                      formatMode === m.key
                        ? "border-primary bg-primary/5"
                        : "border-border/70 hover:bg-muted/40"
                    }`}
                    data-testid={`format-mode-${m.key.replace("_", "-")}`}
                  >
                    <input
                      type="radio"
                      name="format_mode_radio"
                      className="mt-1"
                      checked={formatMode === m.key}
                      onChange={() => setFormatMode(m.key)}
                      data-testid={`format-mode-${m.key.replace("_", "-")}-input`}
                    />
                    <span className="flex flex-col leading-tight">
                      <span className="font-medium">{m.label}</span>
                      <span className="text-xs text-muted-foreground mt-0.5">
                        — {m.hint}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {strategy === "consumption" && (
              <div className="text-xs text-muted-foreground">
                This will generate PO drafts from{" "}
                <b>daily-scope items whose status is out-of-stock, danger, or reorder</b>.
                Inactive items (no sales in the last 90 days) are excluded.
                You&apos;ll see a preview before anything is persisted.
              </div>
            )}
            {strategy === "file_rules" && (
              <div
                className="rounded-md border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground"
                data-testid="strategy-file-rules-note"
              >
                File rules mode ignores sales-based heuristics. Every SKU with
                a config row is evaluated: when current stock &lt; reorder
                level, we suggest the configured standard order quantity in
                its native purchase unit. Manual-locked config rows and
                per-location applicability overrides still apply.
              </div>
            )}
            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} data-testid="wizard-next-1">Next →</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <CardHeader><div className="text-sm font-medium">2 · Scope</div></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <button
                className={`px-3 py-1.5 text-sm rounded-md border ${
                  scope === "company" ? "bg-foreground text-background" : "border-border/70 hover:bg-muted"
                }`}
                onClick={() => setScope("company")}
                data-testid="scope-company"
              >
                All PO-target locations
              </button>
              <button
                className={`px-3 py-1.5 text-sm rounded-md border ${
                  scope === "location" ? "bg-foreground text-background" : "border-border/70 hover:bg-muted"
                }`}
                onClick={() => setScope("location")}
                data-testid="scope-location"
              >
                Specific locations
              </button>
            </div>
            {scope === "location" && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-64 overflow-auto border rounded-md p-3 border-border/70">
                {locations.map((l) => (
                  <label key={l.location_id} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedLocations.includes(l.location_id)}
                      onChange={() => toggleLoc(l.location_id)}
                      data-testid={`loc-cb-${l.location_id}`}
                    />
                    {l.display_name}
                  </label>
                ))}
              </div>
            )}
            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep(1)}>← Back</Button>
              <Button onClick={() => setStep(3)} data-testid="wizard-next-2">Next →</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 3 && (
        <Card>
          <CardHeader><div className="text-sm font-medium">3 · Status filter</div></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Which item statuses should we generate POs for?
            </p>
            <div className="grid grid-cols-2 gap-2">
              {STATUSES.map((s) => (
                <label
                  key={s.key}
                  className={`flex items-center gap-2 border rounded-md px-3 py-2 cursor-pointer text-sm ${
                    statusSet.has(s.key) ? "border-primary bg-primary/5" : "border-border/70 hover:bg-muted/40"
                  }`}
                  data-testid={`status-cb-${s.key}`}
                >
                  <input
                    type="checkbox"
                    checked={statusSet.has(s.key)}
                    onChange={() => toggleStatus(s.key)}
                  />
                  {s.label}
                </label>
              ))}
            </div>
            <div className="pt-2">
              <label className="text-xs uppercase tracking-wider text-muted-foreground">
                Category filter (optional)
              </label>
              <Input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="e.g. Grocery"
                className="text-sm mt-1 max-w-xs"
                data-testid="category-input"
              />
            </div>
            <div className="pt-2 space-y-1.5">
              <label className="text-xs uppercase tracking-wider text-muted-foreground">
                Item scope
              </label>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="item_scope_radio"
                    checked={itemScope === "daily"}
                    onChange={() => setItemScope("daily")}
                    data-testid="autosuggest-scope-daily"
                  />
                  <span>Daily items only <span className="text-muted-foreground text-xs">(default)</span></span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={itemScope === "all"}
                    onChange={(e) => setItemScope(e.target.checked ? "all" : "daily")}
                    disabled={itemScope === "bar"}
                    data-testid="autosuggest-include-bar"
                  />
                  <span>Include bar items (Kasol 2 destination)</span>
                </label>
                {itemScope === "bar" && (
                  <span
                    className="text-xs rounded-full bg-brand-subtle text-brand-hover px-2 py-0.5 font-medium"
                    data-testid="autosuggest-bar-only"
                  >
                    Bar only (deep-linked)
                  </span>
                )}
              </div>
            </div>
            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep(2)}>← Back</Button>
              <Button onClick={() => setStep(4)} data-testid="wizard-next-3">Next →</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 4 && (
        <Card>
          <CardHeader><div className="text-sm font-medium">4 · Vendor selection</div></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <button
                className={`px-3 py-1.5 text-sm rounded-md border ${
                  vendorSelection === "cheapest" ? "bg-foreground text-background" : "border-border/70 hover:bg-muted"
                }`}
                onClick={() => setVendorSelection("cheapest")}
                data-testid="vsel-cheapest"
              >
                Cheapest per SKU
              </button>
              <button
                className={`px-3 py-1.5 text-sm rounded-md border ${
                  vendorSelection === "explicit" ? "bg-foreground text-background" : "border-border/70 hover:bg-muted"
                }`}
                onClick={() => setVendorSelection("explicit")}
                data-testid="vsel-explicit"
              >
                Only these vendors
              </button>
            </div>
            {vendorSelection === "explicit" && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-64 overflow-auto border rounded-md p-3 border-border/70">
                {vendors.map((v) => (
                  <label key={v.vendor_id} className="flex items-center gap-2 text-xs cursor-pointer">
                    <input
                      type="checkbox"
                      checked={explicitVendors.includes(v.vendor_id)}
                      onChange={() => toggleVendor(v.vendor_id)}
                      data-testid={`vendor-cb-${v.vendor_id}`}
                    />
                    {v.name}
                  </label>
                ))}
              </div>
            )}
            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep(3)}>← Back</Button>
              <Button
                onClick={runPreview}
                disabled={loading}
                data-testid="wizard-preview-btn"
              >
                {loading ? "Computing…" : "Preview draft POs →"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 5 && preview && (
        <Card>
          <CardHeader>
            <div className="text-sm font-medium">5 · Review draft POs</div>
          </CardHeader>
          <CardContent className="space-y-4">
            {preview.drafts.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center" data-testid="preview-empty">
                No items match these filters — either everything&apos;s
                sufficiently stocked, or no vendor rates cover them.
                Check the warnings below.
              </div>
            ) : (
              <>
                <div className="text-sm">
                  <b>{preview.aggregates?.draft_count}</b> draft PO{preview.aggregates?.draft_count === 1 ? "" : "s"} —{" "}
                  <b>{preview.aggregates?.line_count_total}</b> line{preview.aggregates?.line_count_total === 1 ? "" : "s"}{" "}
                  · total <b>{fmtINR(preview.aggregates?.total_amount)}</b>
                </div>
                {preview.summary && (
                  <div
                    className="rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-xs flex flex-wrap items-center gap-x-4 gap-y-1"
                    data-testid="preview-format-split"
                  >
                    <span
                      className={`rounded-full px-2 py-0.5 font-medium ${
                        preview.summary.strategy === "file_rules"
                          ? "bg-emerald-100 text-emerald-800"
                          : "bg-primary/10 text-primary"
                      }`}
                      data-testid="preview-strategy-chip"
                    >
                      Strategy:{" "}
                      {preview.summary.strategy === "file_rules"
                        ? "File rules"
                        : "Consumption"}
                    </span>
                    <span
                      className="rounded-full bg-primary/10 text-primary px-2 py-0.5 font-medium"
                      data-testid="preview-format-mode-chip"
                    >
                      Format mode:{" "}
                      {FORMAT_MODE_LABELS[preview.summary.format_mode]
                        || preview.summary.format_mode
                        || "Auto"}
                    </span>
                    <span className="text-muted-foreground">Format split:</span>
                    <span data-testid="preview-pan-india-count">
                      <b>{preview.summary.pan_india_pos}</b> Pan-India
                    </span>
                    <span data-testid="preview-location-wise-count">
                      <b>{preview.summary.location_wise_pos}</b> Location-wise
                    </span>
                    <span className="text-muted-foreground">
                      across <b>{preview.summary.vendors}</b> vendor{preview.summary.vendors === 1 ? "" : "s"}
                    </span>
                    {preview.summary.strategy === "file_rules" && (
                      <span className="text-muted-foreground w-full mt-1">
                        Evaluated <b>{preview.summary.config_rows_evaluated}</b>{" "}
                        config rows; <b>{preview.summary.trigger_count}</b>{" "}
                        (sku × outlet) below reorder level.
                      </span>
                    )}
                  </div>
                )}
                {preview.summary?.format_mode === "per_location" && (
                  <div
                    className="text-xs text-muted-foreground -mt-2"
                    data-testid="preview-format-mode-explainer"
                  >
                    You chose &ldquo;One PO per location&rdquo; — generating{" "}
                    <b>{preview.aggregates?.draft_count}</b> PO
                    {preview.aggregates?.draft_count === 1 ? "" : "s"} (one per
                    vendor × outlet pair).
                  </div>
                )}
                {preview.summary?.format_mode === "combined" && (
                  <div
                    className="text-xs text-muted-foreground -mt-2"
                    data-testid="preview-format-mode-explainer"
                  >
                    You chose &ldquo;Combined per vendor&rdquo; — generating one
                    Pan-India PO per vendor covering all outlets.
                  </div>
                )}
                <div className="space-y-3">
                  {preview.drafts.map((d, i) => (
                    <div key={i} className="border rounded-md p-3 border-border/70" data-testid={`draft-preview-${i}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{d.vendor_snapshot?.name || d.vendor_id}</div>
                          <div className="text-xs text-muted-foreground">
                            {d.line_items.length} line{d.line_items.length === 1 ? "" : "s"} ·{" "}
                            {d.destination_locations.length} location{d.destination_locations.length === 1 ? "" : "s"}
                          </div>
                        </div>
                        <div className="text-sm font-medium">{fmtINR(d.totals?.total_amount)}</div>
                      </div>
                      <details className="mt-2">
                        <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                          Show {d.line_items.length} line item{d.line_items.length === 1 ? "" : "s"}
                        </summary>
                        <div className="text-xs mt-2 max-h-40 overflow-auto">
                          {d.line_items.map((li, j) => {
                            // Phase 9.7 (fix) — file-rules lines carry
                            // `native_order = {qty, unit, measuring_qty}`.
                            // Render the purchase-unit shape ("10 pkts")
                            // when present, with an info-icon tooltip
                            // revealing the measuring-unit equivalent
                            // ("= 5000 pcs"). Consumption-mode lines
                            // (no native_order) keep the pre-9.7 shape.
                            const nat = li.native_order;
                            const measUnit = li.item_snapshot?.uom || "";
                            const measQty = nat?.measuring_qty
                              ?? nat?.measuring_qty_per_location
                              ?? li.quantity;
                            return (
                              <div
                                key={j}
                                className="flex justify-between gap-4 py-0.5 border-b border-border/40 last:border-0"
                                data-testid={`preview-line-${i}-${j}`}
                              >
                                <span className="truncate">
                                  {li.item_snapshot?.name || li.item_sku}
                                </span>
                                <span className="tabular-nums flex items-center gap-1">
                                  {nat && nat.unit ? (
                                    <span
                                      className="inline-flex items-center gap-0.5"
                                      title={`= ${fmtQty(measQty)} ${measUnit} (in measuring unit)`}
                                      data-testid={`preview-line-native-${i}-${j}`}
                                    >
                                      {fmtQty(nat.qty)} {nat.unit}
                                      <Info className="w-2.5 h-2.5 opacity-70" />
                                    </span>
                                  ) : (
                                    <span>{fmtQty(li.quantity)}</span>
                                  )}
                                  <span className="text-muted-foreground">
                                    × {fmtINR(li.unit_rate)}
                                  </span>
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </details>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* Warnings */}
            {(preview.warnings?.no_vendor_rate?.length > 0 ||
              preview.warnings?.no_vendor_after_filter?.length > 0) && (
              <div className="border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/40 rounded-md p-3 text-xs" data-testid="preview-warnings">
                <b>Warnings:</b>
                {preview.warnings?.no_vendor_rate?.length > 0 && (
                  <div className="mt-1">
                    <span className="opacity-70">Items with no vendor rate ({preview.warnings.no_vendor_rate.length}):</span>{" "}
                    {preview.warnings.no_vendor_rate.slice(0, 5).map((w) => w.name || w.sku).join(", ")}
                    {preview.warnings.no_vendor_rate.length > 5 && ` … +${preview.warnings.no_vendor_rate.length - 5} more`}
                  </div>
                )}
                {preview.warnings?.no_vendor_after_filter?.length > 0 && (
                  <div className="mt-1">
                    <span className="opacity-70">Excluded by vendor filter ({preview.warnings.no_vendor_after_filter.length}):</span>{" "}
                    {preview.warnings.no_vendor_after_filter.slice(0, 5).map((w) => w.name || w.sku).join(", ")}
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between pt-2">
              <Button variant="ghost" onClick={() => setStep(4)}>← Back</Button>
              <Button
                onClick={commit}
                disabled={submitting || preview.drafts.length === 0}
                data-testid="wizard-commit-btn"
              >
                {submitting ? "Saving…" : `Generate ${preview.drafts.length} Draft PO${preview.drafts.length === 1 ? "" : "s"}`}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
